import { Fragment, useRef, useState } from 'react';
import { Card, FileIcon, Hr, Input, Muted, fileIconKind } from './ui';
import { formatBytes } from './formatBytes';


// Reduce a MIME like "video/quicktime" or "image/webp" to a friendly category.
// Returns null for types we don't have a clean label for — caller hides the
// type slot entirely rather than showing raw subtypes.
export function simplifyMime(mime: string | undefined): string | null {
  if (!mime) return null;
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return null;
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

export default function FileDropzone(props: {
  files: File[];
  onFiles: (files: File[]) => void;
  onRemove: (idx: number) => void;
}) {
  const { files, onFiles, onRemove } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [drag, setDrag] = useState(false);

  return (
    <>
      <Card
        style={{
          padding: 14,
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: drag ? 'rgba(210,180,91,0.65)' : 'rgba(210,180,91,0.25)',
          background: drag ? 'rgba(210,180,91,0.06)' : 'rgba(0,0,0,0.10)',
          cursor: 'pointer',
        }}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(false);
          const list = Array.from(e.dataTransfer.files ?? []);
          if (list.length) onFiles(list);
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 14 }}>Drag & drop files here</div>
        <Muted style={{ marginTop: 6 }}>or click to select files.</Muted>
        <Input
          ref={inputRef as any}
          type="file"
          multiple
          onChange={(e) => {
            const list = Array.from(e.target.files ?? []);
            if (list.length) onFiles(list);
            // allow selecting same file again
            e.currentTarget.value = '';
          }}
          style={{ display: 'none' }}
        />
      </Card>

      {files.length ? (
        <div style={{ marginTop: 10, display: 'grid' }}>
          {files.map((f, idx) => (
            <Fragment key={`${f.name}:${f.size}:${f.lastModified}`}>
              {idx > 0 ? <Hr style={{ margin: '8px 0' }} /> : null}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div
                    aria-hidden="true"
                    style={{
                      flex: '0 0 auto',
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(210, 166, 103, 0.08)',
                      border: '1px solid rgba(210, 166, 103, 0.18)',
                      color: 'rgba(233, 221, 191, 0.8)',
                    }}
                  >
                    <FileIcon kind={fileIconKind(f.type, f.name)} size={18} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                    <Muted style={{ fontSize: 12, margin: 0 }}>{formatBytes(f.size)}</Muted>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  aria-label={`Remove ${f.name}`}
                  style={{
                    appearance: 'none',
                    background: 'transparent',
                    border: 'none',
                    color: 'rgba(233, 221, 191, 0.55)',
                    cursor: 'pointer',
                    padding: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 6,
                    transition: 'color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255, 130, 130, 0.95)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(233, 221, 191, 0.55)'; }}
                >
                  <TrashIcon />
                </button>
              </div>
            </Fragment>
          ))}
        </div>
      ) : null}
    </>
  );
}
